import { LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import { Button, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import { logger } from '@/services/logger.service';

import './ImageWithLoader.scss';

interface ImageWithLoaderProps {
  fetchImage?: () => Promise<Blob>;
  src?: string;
  alt: string;
  onClick: (url: string) => void;
  className?: string;
  onLoad?: () => void;
}

const ImageWithLoader = ({
  fetchImage,
  src,
  alt,
  className = '',
  onClick,
  onLoad,
}: ImageWithLoaderProps) => {
  const [imageSrc, setImageSrc] = useState<string | null>(src || null);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const loadImage = useCallback(async () => {
    if (!fetchImage) return;

    let revokedUrl: string | null = null;
    try {
      setHasError(false);
      const blob = await fetchImage();
      if (blob instanceof Blob) {
        const url = URL.createObjectURL(blob);
        setImageSrc(url);
        revokedUrl = url;
      } else {
        logger.warn('Image fetch error.');
        setHasError(true);
      }
    } catch (err) {
      logger.warn('Image fetch error:', err);
      setHasError(true);
    }

    return () => {
      if (revokedUrl) {
        URL.revokeObjectURL(revokedUrl);
      }
    };
  }, []);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 },
    );
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (isInView) {
      loadImage();
    }
  }, [loadImage, isInView]);

  if (hasError) {
    return (
      <div className="image-error-box">
        <div className="image-error-message">
          <p className="image-error-text">Image failed to load</p>
          <Button
            type="link"
            icon={<ReloadOutlined />}
            onClick={loadImage}
            className="reload-button"
            aria-label="Reload image"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="image-container" ref={containerRef}>
      {imageSrc && (
        <>
          <img
            src={imageSrc}
            alt={alt}
            className={`${className}`}
            onClick={() => onClick(imageSrc)}
            onError={() => setHasError(true)}
            onLoad={() => {
              if (onLoad) onLoad();
              setIsLoaded(true);
              setHasError(false);
            }}
          />
          {!isLoaded && (
            <div className="loader-overlay">
              <Spin
                indicator={
                  <LoadingOutlined
                    style={{ fontSize: 24, color: 'white' }}
                    spin
                  />
                }
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ImageWithLoader;
